'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { Menu } from '@base-ui/react/menu';

// ---------------------------------------------------------------------------
// MessageActionsMenu — Base UI Menu wrapped for use inside <MessageActions>.
//
// These are thin forward-ref wrappers that expose the full Base UI Menu API
// under the `MessageActionsMenu` (and `Message.ActionsMenu`) namespace.
//
// Why wrappers and not direct re-exports?
// - Consistent naming convention with the rest of x-chat-headless
// - Allows future extensibility (e.g. reading MessageContext, injecting
//   ownerState-derived props, adding data attributes)
// - Explicit public API surface that we control
//
// Accessibility provided by Base UI Menu:
// - `role="menu"` on MenuPopup, `role="menuitem"` on MenuItem
// - Full keyboard navigation: Arrow Up/Down, Home/End, Escape
// - Roving tabIndex focus management
// - `aria-haspopup="menu"` and `aria-expanded` on MenuTrigger
// - Focus restoration to trigger on close
// ---------------------------------------------------------------------------

/**
 * The root of a message action menu. Wraps Base UI `Menu.Root`.
 * Does not render an HTML element itself.
 *
 * Usage:
 * ```tsx
 * <MessageActionsMenu.Root>
 *   <MessageActionsMenu.Trigger>⋮</MessageActionsMenu.Trigger>
 *   <MessageActionsMenu.Positioner>
 *     <MessageActionsMenu.Popup>
 *       <MessageActionsMenu.Item onClick={handleCopy}>Copy</MessageActionsMenu.Item>
 *       <MessageActionsMenu.Item onClick={handleDelete}>Delete</MessageActionsMenu.Item>
 *     </MessageActionsMenu.Popup>
 *   </MessageActionsMenu.Positioner>
 * </MessageActionsMenu.Root>
 * ```
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function MessageActionsMenuRoot(props) {
  return /*#__PURE__*/_jsx(Menu.Root, _extends({}, props));
}

// ---------------------------------------------------------------------------

/**
 * A button that opens the message action menu. Wraps Base UI `Menu.Trigger`.
 * Renders a `<button>` element.
 *
 * Automatically receives `aria-haspopup="menu"` and `aria-expanded` from
 * Base UI.
 */

export const MessageActionsMenuTrigger = /*#__PURE__*/React.forwardRef(function MessageActionsMenuTrigger(props, ref) {
  return /*#__PURE__*/_jsx(Menu.Trigger, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------

/**
 * Positions the menu popup relative to the trigger.
 * Wraps Base UI `Menu.Positioner`. Renders a `<div>` element.
 *
 * Accepts all Base UI anchor-positioning props (`side`, `align`,
 * `sideOffset`, `alignOffset`, `collisionBoundary`, etc.).
 */
if (process.env.NODE_ENV !== "production") MessageActionsMenuTrigger.displayName = "MessageActionsMenuTrigger";
export const MessageActionsMenuPositioner = /*#__PURE__*/React.forwardRef(function MessageActionsMenuPositioner(props, ref) {
  return /*#__PURE__*/_jsx(Menu.Positioner, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------

/**
 * The container for menu items. Wraps Base UI `Menu.Popup`.
 * Renders a `<div>` with `role="menu"`.
 */
if (process.env.NODE_ENV !== "production") MessageActionsMenuPositioner.displayName = "MessageActionsMenuPositioner";
export const MessageActionsMenuPopup = /*#__PURE__*/React.forwardRef(function MessageActionsMenuPopup(props, ref) {
  return /*#__PURE__*/_jsx(Menu.Popup, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------

/**
 * An individual action item inside the menu. Wraps Base UI `Menu.Item`.
 * Renders a `<div>` with `role="menuitem"`.
 *
 * Receives `data-highlighted` when focused via keyboard or pointer.
 */
if (process.env.NODE_ENV !== "production") MessageActionsMenuPopup.displayName = "MessageActionsMenuPopup";
export const MessageActionsMenuItem = /*#__PURE__*/React.forwardRef(function MessageActionsMenuItem(props, ref) {
  return /*#__PURE__*/_jsx(Menu.Item, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------

/**
 * Groups a set of related menu items together.
 * Wraps Base UI `Menu.Group`. Renders a `<div>` element.
 */
if (process.env.NODE_ENV !== "production") MessageActionsMenuItem.displayName = "MessageActionsMenuItem";
export const MessageActionsMenuGroup = /*#__PURE__*/React.forwardRef(function MessageActionsMenuGroup(props, ref) {
  return /*#__PURE__*/_jsx(Menu.Group, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------

/**
 * A label for a group of menu items.
 * Wraps Base UI `Menu.GroupLabel`. Renders a `<div>` element.
 */
if (process.env.NODE_ENV !== "production") MessageActionsMenuGroup.displayName = "MessageActionsMenuGroup";
export const MessageActionsMenuGroupLabel = /*#__PURE__*/React.forwardRef(function MessageActionsMenuGroupLabel(props, ref) {
  return /*#__PURE__*/_jsx(Menu.GroupLabel, _extends({}, props, {
    ref: ref
  }));
});

// ---------------------------------------------------------------------------
// Convenience namespace object, mirrors Base UI's own pattern:
// `MessageActionsMenu.Root`, `.Trigger`, `.Positioner`, `.Popup`, `.Item`, …
// ---------------------------------------------------------------------------
if (process.env.NODE_ENV !== "production") MessageActionsMenuGroupLabel.displayName = "MessageActionsMenuGroupLabel";
export const MessageActionsMenu = {
  Root: MessageActionsMenuRoot,
  Trigger: MessageActionsMenuTrigger,
  Positioner: MessageActionsMenuPositioner,
  Popup: MessageActionsMenuPopup,
  Item: MessageActionsMenuItem,
  Group: MessageActionsMenuGroup,
  GroupLabel: MessageActionsMenuGroupLabel
};